import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from './api.js';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/** Tokens, ko serveris pieņem, kad Turnstile nav konfigurēts (dev/E2E; `verifyTurnstile`). */
export const DEV_TURNSTILE_TOKEN = 'dev';

export type TurnstileStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface TurnstileState {
  /** true = serverim ir site-key un logrīks jārenderē. */
  enabled: boolean;
  status: TurnstileStatus;
  /** Tokens: `'dev'` ja logrīks izslēgts; null kamēr site-key nav zināma vai izaicinājums nav atrisināts. */
  token: string | null;
  containerRef: (el: HTMLDivElement | null) => void;
  reset: () => void;
}

let siteKeyPromise: Promise<string | null> | null = null;

/** Site-key tiek prasīta vienu reizi uz lapas ielādi — ir publiska un nemainās. */
function fetchSiteKey(): Promise<string | null> {
  if (!siteKeyPromise) {
    siteKeyPromise = api
      .get<{ siteKey?: string | null }>('/api/auth/turnstile')
      .then((res) => (res.ok ? (res.data.siteKey ?? null) : null))
      .catch(() => null);
  }
  return siteKeyPromise;
}

let scriptPromise: Promise<TurnstileApi> | null = null;

function loadScript(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = () => {
        if (window.turnstile) resolve(window.turnstile);
        else reject(new Error('turnstile_unavailable'));
      };
      script.onerror = () => {
        scriptPromise = null;
        script.remove();
        reject(new Error('turnstile_unavailable'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

/**
 * Cloudflare Turnstile logrīks pieslēgšanās/reģistrācijas formām.
 * Bez site-key (lokāli, E2E) neveic nevienu izsaukumu uz Cloudflare un atdod `token: 'dev'`.
 */
export function useTurnstile(): TurnstileState {
  const { i18n } = useTranslation();
  const [siteKey, setSiteKey] = useState<string | null | undefined>(undefined);
  const [status, setStatus] = useState<TurnstileStatus>('idle');
  const [token, setToken] = useState<string | null>(null);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | undefined>(undefined);
  const language = i18n.language.slice(0, 2);

  useEffect(() => {
    let active = true;
    void fetchSiteKey().then((key) => {
      if (active) setSiteKey(key);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!siteKey || !container) return;
    let active = true;
    setStatus('loading');
    setToken(null);
    loadScript()
      .then((turnstile) => {
        if (!active) return;
        widgetIdRef.current = turnstile.render(container, {
          sitekey: siteKey,
          theme: 'auto',
          language,
          callback: (t) => {
            setToken(t);
            setStatus('ready');
          },
          'error-callback': () => {
            setToken(null);
            setStatus('error');
          },
          'expired-callback': () => setToken(null),
        });
        setStatus('ready');
      })
      .catch(() => {
        if (active) setStatus('error');
      });
    return () => {
      active = false;
      const id = widgetIdRef.current;
      widgetIdRef.current = undefined;
      if (id !== undefined) window.turnstile?.remove(id);
    };
  }, [siteKey, container, language]);

  const reset = useCallback(() => {
    setToken(null);
    const id = widgetIdRef.current;
    if (id !== undefined) {
      window.turnstile?.reset(id);
      setStatus('ready');
    }
  }, []);

  const enabled = Boolean(siteKey);
  const resolvedToken = siteKey === undefined ? null : enabled ? token : DEV_TURNSTILE_TOKEN;
  return {
    enabled,
    status,
    token: resolvedToken,
    containerRef: setContainer,
    reset,
  };
}
